import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ShieldCheck, ArrowLeft, Mail, KeyRound, Lock, RotateCcw, CheckCircle2, AlertCircle, BadgeCheck } from "lucide-react";
import Seo from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";
import { ClaimBadge } from "@/components/SignalBadges";
import { FadeUp } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#121210] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-emerald-500/50 focus:shadow-[0_0_15px_rgba(16,185,129,0.1)] transition-all duration-200";

// ── Step 1: Claim form ────────────────────────────────────────
function StepForm({ company, onResult }) {
  const [form, setForm] = useState({
    name: "", business_email: "", role: "", website: company?.website || "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (company?.website) setForm((f) => ({ ...f, website: company.website }));
  }, [company?.website]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${company.slug}/claim/init`, form);
      onResult(data.flow, form.business_email, data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <FadeUp delay={0.05}>
      <p className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-emerald-400 mb-3">
        {"// Claim this listing"}
      </p>
      <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-wide text-slate-100 mb-3">
        Own your profile
      </h1>
      <p className="text-sm sm:text-base text-slate-400 leading-relaxed mb-10">
        Verify you represent {company?.name || "this company"} to take control of its profile. Free, always.
      </p>
      <form onSubmit={submit} data-testid="claim-form" className="rounded-2xl border border-white/[0.07] bg-[#0A0A09] p-7 sm:p-9 space-y-5">
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-300 mb-2">Your name <span className="text-rose-500">*</span></label>
            <input data-testid="claim-name-input" value={form.name} onChange={set("name")} required minLength={2} placeholder="Jane Founder" className={inputCls} />
          </div>
          <div>
            <label className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-300 mb-2">Role <span className="text-rose-500">*</span></label>
            <input data-testid="claim-role-input" value={form.role} onChange={set("role")} required minLength={2} placeholder="Founder / CEO / Marketing Lead" className={inputCls} />
          </div>
        </div>
        <div>
          <label className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-300 mb-2">Business email <span className="text-rose-500">*</span></label>
          <input data-testid="claim-email-input" type="email" value={form.business_email} onChange={set("business_email")} required placeholder="jane@yourcompany.com" className={inputCls} />
          <p className="mt-2 text-[11px] text-slate-600">Use your company domain email to enable instant OTP verification.</p>
        </div>
        <div>
          <label className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-300 mb-2">Company website</label>
          <input data-testid="claim-website-input" type="url" value={form.website} onChange={set("website")} placeholder="https://yourcompany.com" className={inputCls} />
        </div>
        <div>
          <label className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-300 mb-2">Verification message <span className="text-rose-500">*</span></label>
          <textarea data-testid="claim-message-input" value={form.message} onChange={set("message")} required minLength={10} rows={4} placeholder="Anything that helps confirm you're the right person — LinkedIn, team page, etc." className={`${inputCls} resize-none`} />
        </div>
        {error && <p data-testid="claim-form-error" className="text-sm text-red-400">{error}</p>}
        <button
          type="submit"
          data-testid="claim-submit-button"
          disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-bold rounded-full py-3.5 transition-all duration-200 hover:shadow-[0_0_25px_rgba(16,185,129,0.4)]"
        >
          <ShieldCheck size={16} /> {busy ? "Checking…" : "Continue"}
        </button>
      </form>
    </FadeUp>
  );
}

// ── Step 2: OTP entry ─────────────────────────────────────────
function StepOtp({ company, email, onSuccess, onManual }) {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const refs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(60);
  const [resending, setResending] = useState(false);

  // countdown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleDigit = (i, val) => {
    const d = val.replace(/\D/g, "").slice(-1);
    const next = [...otp];
    next[i] = d;
    setOtp(next);
    if (d && i < 5) refs[i + 1].current?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) refs[i - 1].current?.focus();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(""));
      refs[5].current?.focus();
    }
  };

  const submit = async () => {
    const code = otp.join("");
    if (code.length !== 6) { setError("Please enter all 6 digits."); return; }
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${company.slug}/claim/verify-otp`, { email, otp: code });
      if (data.flow === "need_password") onSuccess("need_password");
      else if (data.flow === "pending_review") onSuccess("pending_review", data);
      else if (data.flow === "manual_review") onManual(data);
    } catch (err) {
      setError(formatApiError(err));
      setOtp(["", "", "", "", "", ""]);
      refs[0].current?.focus();
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post(`/companies/${company.slug}/claim/resend-otp`, { email });
      setResendCooldown(60);
      setOtp(["", "", "", "", "", ""]);
      setError("");
      refs[0].current?.focus();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <FadeUp>
      <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 mb-6">
        <Mail size={22} className="text-emerald-400" />
      </div>
      <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-wide text-slate-100 mb-3">
        Check your email
      </h1>
      <p className="text-sm text-slate-400 leading-relaxed mb-2">
        We sent a 6-digit code to <span className="text-slate-200 font-medium">{email}</span>
      </p>
      <p className="text-xs text-slate-600 mb-8">It expires in 15 minutes.</p>

      <div className="rounded-2xl border border-white/[0.07] bg-[#0A0A09] p-7 sm:p-9 space-y-6">
        {/* OTP input boxes */}
        <div className="flex gap-3 justify-center" onPaste={handlePaste}>
          {otp.map((digit, i) => (
            <input
              key={i}
              ref={refs[i]}
              data-testid={`otp-digit-${i}`}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleDigit(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              className="w-11 h-14 text-center text-2xl font-bold text-slate-100 rounded-xl border border-white/10 bg-[#121210] focus:border-emerald-500/60 focus:shadow-[0_0_15px_rgba(16,185,129,0.15)] outline-none transition-all"
            />
          ))}
        </div>

        {error && (
          <p data-testid="otp-error" className="flex items-center gap-2 text-sm text-red-400">
            <AlertCircle size={14} /> {error}
          </p>
        )}

        <button
          data-testid="otp-verify-button"
          disabled={busy || otp.join("").length !== 6}
          onClick={submit}
          className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-bold rounded-full py-3.5 transition-all duration-200"
        >
          <KeyRound size={16} /> {busy ? "Verifying…" : "Verify code"}
        </button>

        <div className="flex items-center justify-center">
          {resendCooldown > 0 ? (
            <p className="text-xs text-slate-600 flex items-center gap-1.5">
              <RotateCcw size={11} /> Resend available in {resendCooldown}s
            </p>
          ) : (
            <button
              data-testid="otp-resend-button"
              disabled={resending}
              onClick={resend}
              className="text-xs text-slate-500 hover:text-emerald-400 transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              <RotateCcw size={11} /> {resending ? "Sending…" : "Resend code"}
            </button>
          )}
        </div>
      </div>
    </FadeUp>
  );
}

// ── Step 3: Set password (new users only) ─────────────────────
function StepPassword({ company, email, onSuccess }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${company.slug}/claim/complete`, { email, password });
      onSuccess(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <FadeUp>
      <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 mb-6">
        <Lock size={22} className="text-emerald-400" />
      </div>
      <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-wide text-slate-100 mb-3">
        Create your account
      </h1>
      <p className="text-sm text-slate-400 leading-relaxed mb-8">
        Set a password for <span className="text-slate-200 font-medium">{email}</span> — your HighRank owner account.
      </p>
      <form onSubmit={submit} data-testid="claim-password-form" className="rounded-2xl border border-white/[0.07] bg-[#0A0A09] p-7 sm:p-9 space-y-5">
        <div>
          <label className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-300 mb-2">Email</label>
          <input type="email" value={email} readOnly className={`${inputCls} opacity-50 cursor-not-allowed`} />
        </div>
        <div>
          <label className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-300 mb-2">Password <span className="text-rose-500">*</span></label>
          <input
            data-testid="claim-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            placeholder="Min 8 characters"
            className={inputCls}
          />
        </div>
        <div>
          <label className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-300 mb-2">Confirm password <span className="text-rose-500">*</span></label>
          <input
            data-testid="claim-confirm-password-input"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            placeholder="Repeat password"
            className={inputCls}
          />
        </div>
        {error && <p data-testid="claim-password-error" className="text-sm text-red-400">{error}</p>}
        <button
          type="submit"
          data-testid="claim-create-account-button"
          disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-bold rounded-full py-3.5 transition-all"
        >
          <ShieldCheck size={16} /> {busy ? "Creating account…" : "Claim this listing"}
        </button>
      </form>
    </FadeUp>
  );
}

// ── Pending review screen ─────────────────────────────────────
function StepPending({ company, companyName, verification }) {
  const name = companyName || company?.name;
  return (
    <FadeUp>
      <div data-testid="claim-pending-panel" className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.05] p-10 text-center">
        <CheckCircle2 size={40} className="text-amber-400 mx-auto mb-5" />
        <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-wide text-slate-100 mb-3">
          Claim submitted
        </h1>
        <p className="text-sm text-slate-400 leading-relaxed max-w-md mx-auto mb-4">
          {verification === "otp"
            ? "Your email is verified. "
            : "We'll verify your details manually. "}
          Your claim for <span className="text-slate-200 font-semibold">{name}</span> is now with our team for approval.
        </p>
        <p className="text-xs text-slate-500 leading-relaxed max-w-md mx-auto mb-8">
          You're signed in — you can edit your listing details right away. The ownership badge, media uploads and boost placements go live once an admin approves your claim.
        </p>
        <Link
          to={company ? `/company/${company.slug}` : "/leaderboard"}
          data-testid="claim-pending-back-button"
          className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm rounded-full px-7 py-3 transition-colors duration-200"
        >
          Go to {name || "leaderboard"}
        </Link>
      </div>
    </FadeUp>
  );
}

// ── Manual review fallback ─────────────────────────────────────
function StepFallback({ company }) {
  return (
    <FadeUp>
      <div data-testid="claim-fallback-panel" className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.05] p-10 text-center">
        <AlertCircle size={40} className="text-amber-400 mx-auto mb-5" />
        <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-wide text-slate-100 mb-3">
          We'll verify manually
        </h1>
        <p className="text-sm text-slate-400 leading-relaxed max-w-md mx-auto mb-8">
          We couldn't verify your identity automatically. Your claim request has been submitted for manual review — we'll reach out once it's processed.
        </p>
        <Link
          to={company ? `/company/${company.slug}` : "/leaderboard"}
          data-testid="claim-fallback-back-button"
          className="inline-flex items-center gap-2 border border-white/10 text-slate-300 hover:text-slate-100 text-sm font-bold rounded-full px-7 py-3 transition-colors duration-200"
        >
          Back to {company?.name || "leaderboard"}
        </Link>
      </div>
    </FadeUp>
  );
}

// ── Root page ─────────────────────────────────────────────────
export default function ClaimBusiness() {
  const { slug } = useParams();
  const { setUser } = useAuth();
  const [company, setCompany] = useState(null);
  const [step, setStep] = useState("form");
  const [claimEmail, setClaimEmail] = useState("");
  const [successName, setSuccessName] = useState("");
  const [verification, setVerification] = useState("otp");

  useEffect(() => {
    api.get(`/companies/${slug}`).then((r) => {
      setCompany(r.data);
      if (r.data.ownership_status === "claimed_verified") setStep("already_claimed");
      else if (r.data.ownership_status === "claim_pending") setStep("under_review");
    }).catch(() => {});
  }, [slug]);

  const handleFormResult = (flow, email, data) => {
    setClaimEmail(email);
    if (flow === "otp_sent") {
      setVerification("otp");
      setStep("otp");
    } else {
      setVerification("manual");
      setStep(data?.needs_account ? "password" : "pending");
    }
  };

  const handleOtpSuccess = async (flow, data) => {
    if (flow === "need_password") {
      setStep("password");
    } else if (flow === "pending_review") {
      setSuccessName(data?.company_name || "");
      if (data?.existing_user) {
        api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
      }
      setStep("pending");
    }
  };

  const handlePasswordSuccess = async (data) => {
    setSuccessName(data?.company_name || "");
    if (data?.user) setUser(data.user);
    setStep("pending");
  };

  return (
    <main data-testid="claim-business-page" className="pt-28 md:pt-16 min-h-screen">
      <Seo
        title={`Claim ${company?.name || "your business"} — HighRank`}
        siteName="HighRank"
        description="Claim your company's HighRank listing to manage its profile and reputation."
      />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="relative max-w-2xl mx-auto px-4 sm:px-6 py-12">
          <Link
            to={company ? `/company/${company.slug}` : "/leaderboard"}
            data-testid="claim-back-link"
            className="inline-flex items-center gap-1.5 text-xs font-mono2 uppercase tracking-[0.2em] text-slate-500 hover:text-emerald-400 transition-colors mb-8"
          >
            <ArrowLeft size={13} /> {company ? company.name : "Leaderboard"}
          </Link>

          {company && (
            <div className="flex items-center gap-3 mb-8 rounded-xl border border-white/[0.07] bg-[#0A0A09] p-5">
              <div className="min-w-0 flex-1">
                <p className="font-heading font-bold text-slate-100 truncate">{company.name}</p>
                <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-600 mt-0.5">
                  {company.category} · {company.region}
                </p>
              </div>
              <ClaimBadge claimed={company.claimed} testId="claim-page-badge" />
            </div>
          )}

          {step === "already_claimed" && (
            <FadeUp>
              <div className="rounded-2xl border border-white/[0.07] bg-[#0A0A09] p-10 text-center">
                <BadgeCheck size={36} className="text-emerald-400 mx-auto mb-5" />
                <h1 className="font-display text-3xl uppercase tracking-wide text-slate-100 mb-3">Already Claimed</h1>
                <p className="text-sm text-slate-400 mb-8">This listing has already been claimed by its owner.</p>
                <Link to={`/company/${slug}`} className="inline-flex items-center gap-2 border border-white/10 text-slate-300 hover:text-slate-100 text-sm font-bold rounded-full px-7 py-3 transition-colors">
                  Back to profile
                </Link>
              </div>
            </FadeUp>
          )}

          {step === "form" && company && (
            <StepForm company={company} onResult={handleFormResult} />
          )}

          {step === "otp" && company && (
            <StepOtp
              company={company}
              email={claimEmail}
              onSuccess={handleOtpSuccess}
              onManual={(data) => setStep(data?.needs_account ? "password" : "pending")}
            />
          )}

          {step === "password" && company && (
            <StepPassword company={company} email={claimEmail} onSuccess={handlePasswordSuccess} />
          )}

          {step === "pending" && (
            <StepPending company={company} companyName={successName} verification={verification} />
          )}

          {step === "under_review" && (
            <FadeUp>
              <div data-testid="claim-under-review-panel" className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.05] p-10 text-center">
                <AlertCircle size={36} className="text-amber-400 mx-auto mb-5" />
                <h1 className="font-display text-3xl uppercase tracking-wide text-slate-100 mb-3">Claim under review</h1>
                <p className="text-sm text-slate-400 mb-8">A claim for this listing has already been submitted and is awaiting admin approval.</p>
                <Link to={`/company/${slug}`} className="inline-flex items-center gap-2 border border-white/10 text-slate-300 hover:text-slate-100 text-sm font-bold rounded-full px-7 py-3 transition-colors">
                  Back to profile
                </Link>
              </div>
            </FadeUp>
          )}

          {step === "manual" && (
            <StepFallback company={company} />
          )}
        </div>
      </section>
    </main>
  );
}
